import { IonButton, IonContent, IonIcon, IonPage, useIonToast } from '@ionic/react';
import { eyeOffOutline } from 'ionicons/icons';
import { KeyboardEvent } from 'react';
import { useHistory } from 'react-router';
import CasinoNavBar from 'components/CasinoNavBar';
import { InputEvent } from '../../common/types';
import { toastConfig, ToastType } from '../../config/ToastConfig';
import usePasswords from './hooks/usePasswords';
import { updatePassword } from './updatePassword';
import './change-password.scss';

interface IPasswordField {
  value: string;
  placeholder: string;
  visible: boolean;
  autoComplete: string;
  onChange: (e: InputEvent) => void;
  onToggleVisible: () => void;
}

const PasswordField = ({
  value,
  placeholder,
  visible,
  autoComplete,
  onChange,
  onToggleVisible,
}: IPasswordField) => (
  <div className="change-password-field">
    <img src="assets/icon/change-password/lock.svg" alt="" className="change-password-field__icon" />
    <input
      type={visible ? 'text' : 'password'}
      value={value}
      placeholder={placeholder}
      autoComplete={autoComplete}
      onChange={onChange}
    />
    <button
      type="button"
      className="change-password-field__toggle"
      aria-label={visible ? 'Hide password' : 'Show password'}
      onClick={onToggleVisible}
    >
      {visible ? (
        <IonIcon icon={eyeOffOutline} />
      ) : (
        <img src="assets/icon/change-password/eye.svg" alt="" />
      )}
    </button>
  </div>
);

const ChangePassword = () => {
  const history = useHistory();
  const {
    currentPassword,
    newPassword,
    confirmPassword,
    showCurrentPassword,
    showNewPassword,
    showConfirmPassword,
    handleShowCurrentPassword,
    handleShowNewPassword,
    handleShowConfirmPassword,
    handleCurrentPasswordChange,
    handleNewPasswordChange,
    handleConfirmPasswordChange,
    clearFields,
    isPasswordValid,
  } = usePasswords();

  const [presentToast] = useIonToast();
  const showToast = ({ type = 'success', message }: { type?: ToastType; message: string }) => {
    toastConfig.color = type;
    toastConfig['message'] = message;
    void presentToast(toastConfig);
  };

  const handleUpdatePassword = async () => {
    if (newPassword !== confirmPassword) {
      showToast({ type: 'danger', message: 'Failed to match new password' });
      return;
    }

    if (!isPasswordValid()) {
      showToast({ type: 'danger', message: "New password doesn't match requirements" });
      return;
    }

    await updatePassword(currentPassword, newPassword)
      .then(() => {
        showToast({ message: 'Your password has been changed' });
        clearFields();
      })
      .catch((e) => {
        console.error(e);
        showToast({ type: 'danger', message: 'Wrong current password' });
      });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      void handleUpdatePassword();
    }
  };

  return (
    <IonPage className="login-v2-page change-password-page">
      <IonContent fullscreen>
        <div className="login-v2-wrapper">
          <div className="casino-fixed-top__nav login-v2-nav-wrap">
            <CasinoNavBar hideSearch hideLogo hideMenu hideSlotCheckLogo />
            <button
              className="login-v2-back-btn"
              onClick={() => history.goBack()}
              aria-label="Go back"
            />
          </div>
          <div className="login-v2-bg">
            <div className="change-password" onKeyDown={handleKeyDown}>
              <h1 className="change-password__title">
                Change
                <br />
                Password
              </h1>

              <div className="change-password__form">
                <PasswordField
                  value={currentPassword}
                  placeholder="Password"
                  visible={showCurrentPassword}
                  autoComplete="current-password"
                  onChange={handleCurrentPasswordChange}
                  onToggleVisible={handleShowCurrentPassword}
                />
                <PasswordField
                  value={newPassword}
                  placeholder="Enter your new password"
                  visible={showNewPassword}
                  autoComplete="new-password"
                  onChange={handleNewPasswordChange}
                  onToggleVisible={handleShowNewPassword}
                />
                <PasswordField
                  value={confirmPassword}
                  placeholder="Confirm password"
                  visible={showConfirmPassword}
                  autoComplete="new-password"
                  onChange={handleConfirmPasswordChange}
                  onToggleVisible={handleShowConfirmPassword}
                />

                <IonButton
                  expand="block"
                  className="login-v2-submit-btn change-password__submit"
                  disabled={!currentPassword || !newPassword}
                  onClick={() => {
                    void handleUpdatePassword();
                  }}
                >
                  Reset
                </IonButton>

                <p className="change-password__hint">
                  Password must include a number, an uppercase letter, a lowercase letter, and a
                  special character, and be at least 8 characters long
                </p>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ChangePassword;
